// src/components/SEO.js

import React from 'react'
import { Helmet } from 'react-helmet'
import { useStaticQuery, graphql, withPrefix } from 'gatsby'

const SEO = ({ pageTitle, pageDescription, url, ogImage, courses, individualCourse }) => {


  const { site } = useStaticQuery(
    graphql`
      query metadata {
        site {
          siteMetadata {
            author
            description
            ogImageDefault
            organization {
              logo {
                height
                url
                width
              }
              name
              sameAs
              url
            }
            person {
              description
              image {
                caption
                url
              }
              name
              url
            }
            siteUrl
            title
            website {
              inLanguage
              name
              url
            }
          }
        }
      }
    `
  )

  const { title, description, siteUrl, organization, person, website } =
    site.siteMetadata

  const metaDescription = pageDescription || description
  const canonical = url
    ? `${organization.url}${url}`
    : site.siteMetadata.siteUrl
  const ogImg = ogImage || withPrefix(site.siteMetadata.ogImageDefault)

  const websiteStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    url: website.url,
    name: website.name,
    publisher: {
      '@id': `${organization.url}#organization`,
    },
    inLanguage: website.inLanguage,
  }

  const organizationStructuredData = {
    '@context': 'https://schema.org',
    '@type': ['Corporation', 'Organization'],
    '@id': `${organization.url}#organization`,
    name: organization.name,
    url: organization.url,
    sameAs: organization.sameAs,
    logo: {
      '@type': 'ImageObject',
      '@id': `${organization.url}#logo`,
      url: organization.logo.url,
      contentUrl: organization.logo.url,
      caption: organization.name,
      inLanguage: website.inLanguage, // assuming organization language is same as website
      width: organization.logo.width,
      height: organization.logo.height,
    },
  }

  // For individual pages, you would use the below. You need to specify page-specific properties like pageName and pageDescription as required
  const pageStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${siteUrl}${url}#webpage`, // replace 'page' with actual page slug or identifier
    url: siteUrl,
    name: pageTitle || title,
    description: pageDescription || metaDescription,
    isPartOf: {
      '@id': `${website.url}#website`,
    },
    inLanguage: website.inLanguage,
    author: {
      '@type': 'Person',
      '@id': person.url,
      name: person.name,
      description: person.description,
      url: person.url,
      image: {
        '@type': 'ImageObject',
        '@id': person.image.url,
        url: person.image.url,
        caption: person.image.caption,
        inLanguage: website.inLanguage,
      },
      worksFor: {
        '@id': `${organization.url}#organization`,
      },
    },
  }

  const individualCourseStructuredData = {
    '@type': 'ListItem',
    position: 1, // Adjust based on actual position
    item: {
      '@type': 'Course',
      name: pageTitle,
      description: pageDescription,
      provider: {
        '@type': 'Organization',
        name: organization.name,
        sameAs: organization.url,
      },
      inLanguage: website.inLanguage,
      // Add other relevant properties here
    },
  };
  
  const courseList = courses
    ? courses
        .map((course, index) => {
          return {
            '@type': 'ListItem',
            position: index + 1,
            item: {
              '@type': 'Course',
              name: course.metadata.metaTitle,
              description: course.metadata.metaDescription,
              provider: {
                '@type': 'Organization',
                name: organization.name,
                sameAs: organization.url,
              },
              inLanguage: website.inLanguage,
              // ... map other properties similarly
            },
          }
        })
        .filter(Boolean) // Remove null or undefined items
    : []

  const allStructuredData = [
    websiteStructuredData,
    organizationStructuredData,
    pageStructuredData,
    individualCourse && individualCourseStructuredData,
    ...courseList, // Spread to add individual course items
  ].filter(Boolean) // Remove null or undefined items

  return (
    <Helmet
      htmlAttributes={{ lang: website.inLanguage || 'sv-SE' }}
      title={pageTitle || title}
      titleTemplate={`%s | ${site.siteMetadata.title}`}
      meta={[
        { name: 'description', content: pageDescription || metaDescription },
        { property: 'og:title', content: pageTitle || title },
        {
          property: 'og:description',
          content: pageDescription || metaDescription,
        },
        { property: 'og:type', content: 'website' },
        { property: 'og:image', content: ogImg },
        { property: 'og:url', content: canonical },
        { name: 'twitter:card', content: 'summary' },
        { name: 'twitter:creator', content: site.siteMetadata.author },
        { name: 'twitter:title', content: pageTitle || title },
        {
          name: 'twitter:description',
          content: pageDescription || metaDescription,
        },
        // Transferred from Layout
        { property: 'og:type', content: 'business.business' },
        { property: 'og:title', content: title },
        { property: 'og:url', content: '/' },
        { name: 'theme-color', content: '#fff' },
      ]}
    >
      <link rel="canonical" href={canonical} />
      <link
        rel="apple-touch-icon"
        sizes="180x180"
        href={withPrefix('/img/siteimg/apple-touch-icon.png')}
      />
      <link
        rel="icon"
        type="image/png"
        href={withPrefix('/img/siteimg/favicon.png')}
        sizes="32x32"
      />
      <link
        rel="icon"
        type="image/png"
        href={withPrefix('/img/siteimg/favicon.png')}
        sizes="16x16"
      />

      <link
        rel="mask-icon"
        href={withPrefix('/img/siteimg/safari-pinned-tab.svg')}
        color="#557475"
      />
      <script type="application/ld+json">
        {JSON.stringify(allStructuredData)}
      </script>
    </Helmet>
  )
}

export default SEO
